import { useEffect, useRef } from "react"
import { useVirtualizer } from "@tanstack/react-virtual"
import { Send, Check, CheckCheck, X, Info } from "lucide-react"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Progress } from "@/components/ui/progress"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"
import type { ChatMessage } from "./whatsappChat"
import { ToMarkdown } from "../ui/tomarkdown"
import { Popover, PopoverContent, PopoverTrigger, } from "@/components/ui/popover"
import { Badge } from "../ui/badge"
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip"


interface MessageItemProps {
  msg: ChatMessage
  measureRef: (el: Element | null) => void
  virtualItem: { index: number; start: number }
  evaluationItems?: Record<string, Evaluation_Item>
}

export function MessageItem({ evaluationItems = {}, msg, measureRef, virtualItem }: MessageItemProps) {

  const displayContact = msg.contactObject
  const isAssistant = displayContact.type == "ai" //|| displayContact.type == "operator"

  //user_detected_items?: MessageEvaluationItem[]
  const position = msg.direction

  const aiFallback = displayContact.type == "ai" ? <div className="text-lg">🧠</div> : "?"

  msg.escalation_from_level ||= 0
  msg.escalation_to_level ||= 0
  msg.user_detected_items ||= []
  msg.agent_concern_level ||= 0

  let upscale = false
  let downscale = false
  let severity = 0
  let fromLevel = msg.escalation_from_level
  let toLevel = msg.escalation_to_level

  if (!isAssistant) {
    if (toLevel > fromLevel) {
      upscale = true
    } else if (toLevel < fromLevel) {
      downscale = true
    } else {
      severity = fromLevel
    }
  }

  const isScaling = upscale || downscale

  return <div key={msg.id} data-index={virtualItem.index} ref={measureRef} style={{ position: "absolute", top: 0, left: 0, width: "100%", transform: `translateY(${virtualItem.start}px)` }} className="pb-2 px-4">

    <div className={cn("flex items-start gap-2", position == "right" ? "justify-end" : "justify-start")}>

      {position == "left" && <Avatar className="h-7 w-7 shrink-0 mt-1">
        <AvatarImage src={displayContact?.avatar} alt={displayContact?.name} />
        <AvatarFallback className="text-[10px]">{displayContact?.name?.[0]?.toUpperCase() ?? "?"}</AvatarFallback>
      </Avatar>}

      <div className={cn("max-w-[65%] rounded-2xl px-3 py-2 text-sm shadow-xs", position == "right" ? "bg-[#dcf8c6] text-gray-900 rounded-tr-sm shadow-sm" : "bg-white text-gray-900 rounded-tl-sm shadow-sm")}>
        <p className={cn("text-sm font-semibold", position == "right" ? "text-emerald-700 text-right" : "text-emerald-700")}>{displayContact?.name ?? "?"}</p>

        <div className="wrap-break-word whitespace-pre-wrap leading-relaxed"><ToMarkdown>{msg.message}</ToMarkdown></div>


        {!isAssistant && <div className="my-2">

          <div className="flex gap-2 items-center justify-end">

            {isScaling &&
              <>
                <div>
                  <span className="uppercase text-xs">Severity {upscale ? "Escalation" : "De-Escalation"}</span>
                  <span className="mx-1 text-red-500 font-bold">{msg.escalation_from_level}</span>
                  To
                  <span className="mx-1 text-red-500 font-bold">{msg.escalation_to_level}</span>
                </div>
              </>
            }
            {!isScaling && severity > 0 &&
              <>
                <div>
                  <span className="uppercase text-xs">Severity</span>
                  <span className="mx-1 text-red-500 font-bold">{msg.escalation_from_level}</span>
                </div>
              </>
            }

            <div>

              {msg.escalation_reasoning && <Tooltip>
                <TooltipTrigger asChild>
                  <div title="Summary">
                    <Info className="h-4 w-4 cursor-pointer text-blue-600" />
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-h-[50vh] w-[25vw] bg-slate-100 shadow-lg border-slate-400">
                  <div className="w-full overflow-y-auto max-h-[45vh]">
                    {msg.escalation_reasoning}
                  </div>
                </TooltipContent>
              </Tooltip>}
            </div>
          </div>

          <div>
            {(msg.user_detected_items).map((item) => {
              const itemInfo = evaluationItems[item.itemId]
              if (!itemInfo) return null
              return <Tooltip key={item.itemId}>
                <TooltipTrigger>
                  <Badge data-badge variant="outline" className="text-nowrap shrink-0 bg-slate-100 mr-1 mt-2 border-stone-400">
                    {itemInfo.name} : <span className="ml-1 font-bold text-red-500">{item.severity}</span>
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="w-[25vw]">
                  <p className="uppercase text-stone-600 text-xs my-1">Confidence: {item.confidence}</p>
                  <p className="">{item.reasoning}</p>
                </TooltipContent>
              </Tooltip>
            })}
          </div>

        </div>}


        {isAssistant && <div>
          <div className="flex gap-2 items-center justify-end mt-2">

            {msg.agent_concern_level > 0 && <>
              <div title={msg.agent_appropriate ? "Appropriate Response" : "Inappropriate Response"}>
                {msg.agent_appropriate ? "👍" : "👎"}
              </div>
              <div>
                <span className="uppercase text-xs">
                  Concern:
                </span>
                <span className="ml-1 text-red-500 font-bold">
                  {msg.agent_concern_level}
                </span>
              </div>
            </>}

            <div>
              {msg.agent_reasoning && <Tooltip>
                <TooltipTrigger asChild>
                  <div title="Summary">
                    <Info className="h-4 w-4 cursor-pointer text-blue-600" />
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-h-[50vh] w-[25vw] bg-slate-100 shadow-lg border-slate-400">
                  <div className="w-full overflow-y-auto max-h-[45vh]">
                    {msg.agent_reasoning}
                  </div>
                </TooltipContent>
              </Tooltip>}
            </div>
          </div>
          <div>
            {msg.agent_detected_items && msg.agent_detected_items.length > 0 && (msg.agent_detected_items || []).map((item) => {
              const itemInfo = evaluationItems[item.itemId]
              if (!itemInfo) return null
              return <Tooltip>
                <TooltipTrigger>
                  <Badge key={item.itemId} data-badge variant="secondary" className="text-nowrap shrink-0 border-stone-400 mr-1">
                    {itemInfo.name} : <span className="ml-1 font-bold text-red-500">{item.severity}</span>
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="w-[25vw]">
                  <p>{item.reasoning}</p>
                </TooltipContent>
              </Tooltip>
            })}
          </div>
        </div>}


        <div className={cn("flex items-center gap-1 mt-1", position == "right" ? "justify-end" : "justify-start")}>
          {msg.created_at && <span className="text-[10px] text-gray-500">
            {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </span>}
          {isAssistant && <MessageStatusIcon status={msg.status} />}
        </div>
      </div>

      {position == "right" && <Avatar className="h-7 w-7 shrink-0 mb-1">
        <AvatarImage src={displayContact?.avatar} alt={displayContact?.name} />
        <AvatarFallback className="text-[10px] bg-emerald-100 text-emerald-700">{displayContact?.name?.[0]?.toUpperCase() ?? "?"}</AvatarFallback>
      </Avatar>}

    </div>
  </div >

}

function MessageStatusIcon({ status }: { status?: ChatMessage["status"] }) {
  if (status === "sent") return <Check className="h-3 w-3 text-gray-400" />
  if (status === "delivered") return <CheckCheck className="h-3 w-3 text-gray-400" />
  if (status === "read") return <CheckCheck className="h-3 w-3 text-[#53bdeb]" />
  return null
}